import { Link, useParams, Navigate } from "react-router-dom";
import { Home as HomeIcon, Calendar, Phone, ArrowUpRight, Check } from "lucide-react";
import { Seo } from "../components/Seo";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { StickyCall } from "../components/StickyCall";
import { Reveal } from "../components/Reveal";
import { findPost, BLOG_POSTS } from "../data/blog";
import { PHONE, PHONE_HREF } from "../lib/site";

const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
const fmt = (d) => new Date(d).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

export default function BlogPost() {
  const { slug } = useParams();
  const post = findPost(slug);
  if (!post) return <Navigate to="/blog" replace />;

  const related = BLOG_POSTS.filter((p) => p.slug !== slug).slice(0, 3);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: post.title,
      description: post.metaDescription,
      image: post.image,
      datePublished: post.date,
      author: { "@type": "Organization", name: "Urgent Passport & Visa" },
      publisher: { "@type": "Organization", name: "Urgent Passport & Visa" },
      mainEntityOfPage: `https://upvservices.com/blog/${slug}`,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://upvservices.com/" },
        { "@type": "ListItem", position: 2, name: "Blog", item: "https://upvservices.com/blog" },
        { "@type": "ListItem", position: 3, name: post.title, item: `https://upvservices.com/blog/${slug}` },
      ],
    },
  ];

  return (
    <div data-testid="blog-post">
      <Seo title={post.metaTitle} description={post.metaDescription} path={`/blog/${slug}`} jsonLd={jsonLd} />
      <Header />
      <main id="main-content" tabIndex={-1}>
        <section className="relative bg-[#0b192c] text-white overflow-hidden">
          <div className="noise-overlay" />
          <div className="absolute inset-0">
            <img src={post.image} alt={post.title} className="w-full h-full object-cover opacity-20" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0b192c] via-[#0b192c]/90 to-[#0b192c]/50" />
          </div>
          <div className="relative max-w-4xl mx-auto px-6 md:px-8 pt-16 pb-16 md:pt-20 md:pb-20">
            <nav className="flex items-center gap-2 text-sm text-white/60 mb-8" aria-label="Breadcrumb">
              <Link to="/" onClick={scrollTop} className="hover:text-white inline-flex items-center gap-1"><HomeIcon className="w-3.5 h-3.5" /> Home</Link>
              <span>/</span>
              <Link to="/blog" onClick={scrollTop} className="hover:text-white">Blog</Link>
            </nav>
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#ef233c]">{post.category}</span>
            <h1 className="mt-4 font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight max-w-3xl">{post.title}</h1>
            <div className="mt-5 flex items-center gap-4 text-sm text-white/70">
              <span className="inline-flex items-center gap-1.5"><Calendar className="w-4 h-4" /> {fmt(post.date)}</span>
              <span>{post.readTime}</span>
            </div>
          </div>
        </section>

        <section className="bg-white py-16 md:py-24">
          <div className="max-w-3xl mx-auto px-6 md:px-8">
            <p className="text-xl text-slate-700 leading-relaxed mb-12 font-medium">{post.excerpt}</p>
            <div className="space-y-10">
              {post.content.map((b, i) => (
                <Reveal key={i} delay={0.03}>
                  <article>
                    <h2 className="font-display font-bold text-2xl text-[#0b192c] tracking-tight mb-4">{b.h2}</h2>
                    {b.body && <p className="text-slate-600 leading-relaxed text-[17px]">{b.body}</p>}
                    {b.list && (
                      <ul className="mt-4 space-y-2.5">
                        {b.list.map((li, j) => (
                          <li key={j} className="flex items-start gap-3 text-slate-600 text-[17px]">
                            <span className="shrink-0 w-5 h-5 rounded-full bg-[#d90429]/10 flex items-center justify-center mt-1"><Check className="w-3 h-3 text-[#d90429]" /></span>
                            {li}
                          </li>
                        ))}
                      </ul>
                    )}
                  </article>
                </Reveal>
              ))}
            </div>

            <div className="mt-14 rounded-2xl bg-[#0b192c] text-white p-8 md:p-10 relative overflow-hidden">
              <div className="noise-overlay" />
              <div className="relative">
                <h3 className="font-display font-bold text-2xl">Need your documents fast?</h3>
                <p className="text-white/75 mt-2">Talk to a Washington DC passport & visa specialist today.</p>
                <div className="mt-6 flex flex-wrap gap-4">
                  <a href={PHONE_HREF} className="inline-flex items-center gap-2 rounded-full bg-[#d90429] text-white px-6 py-3 font-semibold hover:bg-[#ef233c] transition-colors" data-testid="post-call-btn"><Phone className="w-4 h-4" /> Call {PHONE}</a>
                  <Link to="/contact" onClick={scrollTop} className="inline-flex items-center gap-2 rounded-full border border-white/30 text-white px-6 py-3 font-semibold hover:bg-white hover:text-[#0b192c] transition-colors">Get a Free Quote</Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#f8f9fa] py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-6 md:px-8">
            <h2 className="font-display font-bold text-2xl md:text-3xl text-[#0b192c] tracking-tight mb-10">Keep reading</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {related.map((p) => (
                <Link key={p.slug} to={`/blog/${p.slug}`} onClick={scrollTop} className="group block" data-testid={`post-related-${p.slug}`}>
                  <div className="relative rounded-2xl overflow-hidden h-44 border border-black/5 mb-4">
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#d90429]">{p.category}</span>
                  <h3 className="mt-2 font-display font-bold text-lg text-[#0b192c] leading-tight group-hover:text-[#d90429] transition-colors">{p.title}</h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <StickyCall />
    </div>
  );
}
